import { useEffect } from "react";

const BASE_URL = "https://proofmark-frontend-production.up.railway.app";

function setMeta(selector, attr, value) {
  let el = document.querySelector(selector);
  if (el) el.setAttribute(attr, value);
}

function setOrCreate(selector, attrs) {
  let el = document.querySelector(selector);
  if (!el) {
    el = document.createElement("meta");
    Object.entries(attrs.create || {}).forEach(([k, v]) => el.setAttribute(k, v));
    document.head.appendChild(el);
  }
  if (attrs.content !== undefined) el.setAttribute("content", attrs.content);
}

export default function useSEO({ title, description, path, ogTitle, ogDescription, ogImage, toolDescription }) {
  useEffect(() => {
    const fullUrl = `${BASE_URL}${path}`;
    const resolvedOgTitle = ogTitle || title;
    const resolvedOgDesc = ogDescription || description;

    // Page title
    document.title = title;

    // Meta description
    setMeta('meta[name="description"]', "content", description);

    // Canonical
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.rel = "canonical";
      document.head.appendChild(canonical);
    }
    canonical.href = fullUrl;

    // Open Graph
    setOrCreate('meta[property="og:url"]', {
      create: { property: "og:url" },
      content: fullUrl,
    });
    setOrCreate('meta[property="og:title"]', {
      create: { property: "og:title" },
      content: resolvedOgTitle,
    });
    setOrCreate('meta[property="og:description"]', {
      create: { property: "og:description" },
      content: resolvedOgDesc,
    });
    setOrCreate('meta[property="og:image"]', {
      create: { property: "og:image" },
      content: ogImage || `${BASE_URL}/og-image.png`,
    });
    setOrCreate('meta[name="twitter:image"]', {
      create: { name: "twitter:image" },
      content: ogImage || `${BASE_URL}/og-image.png`,
    });

    // Twitter
    setOrCreate('meta[name="twitter:title"]', {
      create: { name: "twitter:title" },
      content: resolvedOgTitle,
    });
    setOrCreate('meta[name="twitter:description"]', {
      create: { name: "twitter:description" },
      content: resolvedOgDesc,
    });

    // JSON-LD BreadcrumbList — path-aware
    const scriptId = "proofmark-jsonld";
    let script = document.getElementById(scriptId);
    if (!script) {
      script = document.createElement("script");
      script.id = scriptId;
      script.type = "application/ld+json";
      document.head.appendChild(script);
    }

    let breadcrumbs;
    if (path === "/tools") {
      breadcrumbs = [
        { "@type": "ListItem", position: 1, name: "ProofMark", item: BASE_URL },
        { "@type": "ListItem", position: 2, name: "Tools", item: `${BASE_URL}/tools` },
      ];
    } else if (path.startsWith("/tools/")) {
      breadcrumbs = [
        { "@type": "ListItem", position: 1, name: "ProofMark", item: BASE_URL },
        { "@type": "ListItem", position: 2, name: "Tools", item: `${BASE_URL}/tools` },
        { "@type": "ListItem", position: 3, name: title, item: fullUrl },
      ];
    } else if (path === "/blog") {
      breadcrumbs = [
        { "@type": "ListItem", position: 1, name: "ProofMark", item: BASE_URL },
        { "@type": "ListItem", position: 2, name: "Blog", item: `${BASE_URL}/blog` },
      ];
    } else if (path.startsWith("/blog/")) {
      breadcrumbs = [
        { "@type": "ListItem", position: 1, name: "ProofMark", item: BASE_URL },
        { "@type": "ListItem", position: 2, name: "Blog", item: `${BASE_URL}/blog` },
        { "@type": "ListItem", position: 3, name: title, item: fullUrl },
      ];
    } else if (path.startsWith("/glossary/")) {
      // No /glossary index route exists — don't emit a breadcrumb pointing
      // at a URL that redirects to /.
      breadcrumbs = [
        { "@type": "ListItem", position: 1, name: "ProofMark", item: BASE_URL },
        { "@type": "ListItem", position: 2, name: title, item: fullUrl },
      ];
    } else {
      breadcrumbs = [
        { "@type": "ListItem", position: 1, name: "ProofMark", item: BASE_URL },
        { "@type": "ListItem", position: 2, name: title, item: fullUrl },
      ];
    }

    script.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: breadcrumbs,
    });

    // WebApplication schema for individual free tool pages
    const toolScriptId = "proofmark-jsonld-tool";
    if (path.startsWith("/tools/") && path !== "/tools/") {
      let toolScript = document.getElementById(toolScriptId);
      if (!toolScript) {
        toolScript = document.createElement("script");
        toolScript.id = toolScriptId;
        toolScript.type = "application/ld+json";
        document.head.appendChild(toolScript);
      }
      toolScript.textContent = JSON.stringify({
        "@context": "https://schema.org",
        "@type": "WebApplication",
        name: title,
        url: fullUrl,
        description: toolDescription || description,
        applicationCategory: "BusinessApplication",
        operatingSystem: "Web",
        isAccessibleForFree: true,
        offers: {
          "@type": "Offer",
          price: "0",
          priceCurrency: "USD",
        },
        publisher: {
          "@type": "Organization",
          name: "ProofMark",
          url: BASE_URL,
        },
      });
    } else {
      const el = document.getElementById(toolScriptId);
      if (el) el.remove();
    }
  }, [title, description, path, ogTitle, ogDescription, ogImage, toolDescription]);
}
